import { format } from 'date-fns-tz';
import { classes } from 'html-classes';
import { ComponentPropsWithoutRef, ElementType } from 'react';
import { useTranslation } from 'react-i18next';

import { LoyaltyPointsTransaction } from '~/api/Customer';
import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import { ModalType } from '~/components/Modal/interface';
import { useDateLang } from '~/hooks/useDateLang';
import { useModal } from '~/hooks/useModal';
import { useTimeFormat } from '~/hooks/useTimeFormat';

import styles from './History.module.scss';

export type TransactionItemProps<T extends ElementType = 'div'> = {
  as?: T;
  transaction: LoyaltyPointsTransaction;
  interactive?: boolean;
} & Omit<ComponentPropsWithoutRef<T>, 'children'>;

const TransactionItem = <T extends ElementType = 'div'>({
  as,
  transaction,
  className,
  interactive = false,
  ...props
}: TransactionItemProps<T>) => {
  const { t } = useTranslation();
  const { locale } = useDateLang();
  const { timeFormat } = useTimeFormat();
  const { openModal } = useModal();

  const Component = as || 'div';

  return (
    <Component
      {...props}
      className={classes([styles.transactionListItem, className])}
    >
      <div className={styles.content}>
        <div className={styles.mainInfo}>
          <p className={styles.title}>
            {transaction.transactionType === 'manual_adjustment'
              ? t('phrases:present')
              : t('phrases:orderNumber', {
                  number: transaction.order.order_id,
                })}
          </p>
        </div>
        <p className={styles.date}>
          {format(new Date(transaction.createdAt), `dd.MM.yyyy ${timeFormat}`, {
            timeZone: company.config.timeZone,
            locale,
          })}
          {transaction.transactionType !== 'manual_adjustment' && (
            <>
              &nbsp;&#8226;&nbsp;
              <span
                className={classes({
                  [styles.failed!]: transaction.transactionStatus === 'failed',
                })}
              >
                {['pending', 'failed'].includes(transaction.transactionStatus)
                  ? t(
                      `loyalty:progress:full:status:${transaction.transactionStatus}`,
                    )
                  : t(
                      `loyalty:progress:full:type:${transaction.transactionType}`,
                    )}
              </span>
              {interactive && transaction.transactionType === 'refund' && (
                <>
                  &nbsp;
                  <button
                    type="button"
                    className={styles.infoButton}
                    onClick={() =>
                      openModal(ModalType.TransactionDetails, undefined, {
                        transactionId: transaction.id,
                      })
                    }
                    aria-label={t('phrases:orderNumber', {
                      number: transaction.order.order_id,
                    })}
                  >
                    <Icon type="info" size={16} />
                  </button>
                </>
              )}
            </>
          )}
        </p>
      </div>
      <p
        className={classes([
          styles.value,
          transaction.amount > 0 && transaction.transactionType !== 'payment'
            ? transaction.transactionStatus === 'completed'
              ? styles.inc
              : styles.canceled
            : styles.dec,
        ])}
      >
        {transaction.transactionType === 'payment'
          ? `-${transaction.amount}`
          : transaction.amount > 0
            ? `+${transaction.amount}`
            : transaction.amount}
      </p>
    </Component>
  );
};

export default TransactionItem;
